import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import * as EXIF from '@shared/plugins/exif-js/exif';
import * as moment from 'moment';
import { Config } from '@shared/config';
import { Coordinates } from '@shared/interfaces/project/coordinates.interface';
import { FileUploadSuccess } from '@shared/interfaces/project/file-upload-success.interface';
import { PhCropperOutputFile } from '@shared/components/ph-cropper/ph-cropper-output-file.interface';
import { BaseComponent } from '@shared/components/base.component';
import heic2any from 'heic2any';

@Component({
  selector: 'app-ph-upload',
  templateUrl: './ph-upload.component.html',
  styleUrls: ['./ph-upload.component.scss'],
})
export class PhUploadComponent extends BaseComponent implements OnInit {
  @Input() correctForm: UntypedFormGroup = null;
  @Input() required: boolean = false;
  @Input() name: string;
  @Input() styletype: string = 'default';
  @Output() phUploadSuccess: EventEmitter<{ event: string | ArrayBuffer; name: string }> =
    new EventEmitter();
  @Output() phUploadSuccessAfterCropper: EventEmitter<any> = new EventEmitter();
  @Output() phUploadSuccessOptions: EventEmitter<any> = new EventEmitter();
  @Output() phUploadErrorOptions: EventEmitter<any> = new EventEmitter();
  @Input() multiple: boolean = false;
  @Input() checkTags: boolean = false;
  @Input() requiredTags: boolean = true;
  @Input() formDataFile: boolean = false;
  @Input() uploadSize: number = Config.UPLOAD_SIZE;
  @ViewChild('phUploadInput', { static: true }) elDom: ElementRef;
  @ViewChild('checkContentIfEmpty', { static: true }) checkContentIfEmpty: ElementRef;
  formEl: UntypedFormControl = new UntypedFormControl(null);
  formData: FormData = new FormData();
  public loading: boolean = false;
  currentUpload: string | ArrayBuffer = null;
  blobFile: Blob = null;
  uploadSavePack: FileUploadSuccess = null;
  bufforPhoto: string | ArrayBuffer;
  displayConvertHeicImage = false;

  ngOnInit() {
    this.initFormControl();
  }

  constructor() {
    super();
  }

  initFormControl() {
    if (this.correctForm) {
      if (this.required) {
        this.formEl.setValidators([Validators.required]);
      }
      this.correctForm.addControl(this.name, this.formEl);

      const subValueForm = this.formEl.valueChanges.subscribe((value) => {
        if (typeof value === 'string') {
          this.currentUpload = value;
        }
      });
      this.someSubscriptions.add(subValueForm);
    }
  }

  setRequired() {
    this.formEl.setValidators([Validators.required]);
  }

  touched() {
    this.formEl.markAsTouched();
  }

  clearRequired() {
    this.formEl.clearValidators();
  }

  checkDropOver(event) {
    // for DragAndDrop
    event.preventDefault();
  }

  checkDropFile(event) {
    // for DragAndDrop Part 2
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    this.bufforPhoto = null;
    if (this.currentUpload) {
      this.bufforPhoto = this.currentUpload;
    }
    this.onFileChange({ target: { files: [file] } });
  }

  get emptyContent() {
    return !this.loading;
  }

  getArrayBufferFromFile(file) {
    return new Promise(function (resolve) {
      var reader = new FileReader();
      reader.onloadend = function () {
        resolve(reader.result);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  generateSignatureVariants(): number[][] {
    const listOfSignatures = [
      [104, 101, 105, 99],
      [104, 101, 105, 120],
      [104, 101, 118, 99],
      [104, 101, 118, 120],
      [104, 101, 105, 109],
      [104, 101, 105, 115],
      [104, 101, 118, 109],
      [104, 101, 118, 115],
      [109, 105, 102, 49],
      [109, 115, 102, 49],
    ];

    let response = [];

    listOfSignatures.map((signature: number[]) => {
      response.push([...[102, 116, 121, 112], ...[...signature]]);
    });

    return response;
  }

  checkIsHEIC(uint8Array: Uint8Array): boolean {
    const bytes = [...uint8Array].slice(4, 12);
    const signatureVariants = this.generateSignatureVariants();

    for (let index = 0; index < signatureVariants.length; index++) {
      if (this.testVariantAsHeicFile(signatureVariants[index], bytes)) {
        return true;
      }
    }
    return false;
  }

  testVariantAsHeicFile(variant: number[], bytes: number[]): boolean {
    return JSON.stringify(variant) === JSON.stringify(bytes);
  }

  onFileChange(pack) {
    let fileFromUpload: File = pack.target.files[0];
    document.body.style.overflow = 'hidden';

    if (fileFromUpload.size > this.uploadSize) {
      this.toast.error(
        this.translate.instant('PROJECT.image_to_large'),
        this.translate.instant('PROJECT.upload_error')
      );
      this.elDom.nativeElement.value = '';
      return;
    }

    let isHeicPhotoPromise = this.getArrayBufferFromFile(fileFromUpload).then(
      (result: ArrayBuffer) => {
        return Promise.resolve(this.checkIsHEIC(new Uint8Array(result)));
      }
    );

    isHeicPhotoPromise.then((isHeic: boolean) => {
      let filePromise: Promise<File>;

      if (isHeic) {
        this.displayConvertHeicImage = true;

        filePromise = heic2any({ blob: fileFromUpload, toType: 'image/jpeg', quality: 0.8 })
          .then((jpgBlob: Blob) => {
            let newName = fileFromUpload.name.replace(/\.[^/.]+$/, '.jpg');

            this.displayConvertHeicImage = false;
            return Promise.resolve(this.createFileFromHeic(jpgBlob, newName));
          })
          .catch(() => {
            this.toast.error(
              this.translate.instant('PROJECT.convert_error_description'),
              this.translate.instant('PROJECT.convert_error')
            );
            this.displayConvertHeicImage = false;
            return Promise.reject();
          });
      } else {
        filePromise = Promise.resolve(fileFromUpload);
      }

      filePromise.then((file: File | Blob) => {
        this.blobFile = file;
        if (!this.loading && this.blobFile) {
          this.loading = true;
          this.checkTags ? this.checkImageMeta() : this.addCorrectlyImg();
        } else {
          this.toast.error(this.translate.instant('PROJECT.file_error'));
        }
      });
    });
  }

  createFileFromHeic(theBlob: Blob, fileName: string): File {
    let b: any = theBlob;

    b.lastModified = new Date();
    b.name = fileName;

    return <File>theBlob;
  }

  removeFile(event?) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.uploadSavePack = {
      coordinates: null,
      date: null,
    };
    this.formEl.setValue(null);
    this.currentUpload = null;
  }

  fromCropper(pack: PhCropperOutputFile) {
    this.loading = false;
    this.currentUpload = pack.base64 ? pack.base64 : this.bufforPhoto;
    this.phUploadSuccessAfterCropper.emit(pack);
    if (pack.blob && pack.base64) {
      this.formData = new FormData();
      this.blobFile = pack.blob;
      this.formData.append(this.name, this.blobFile);
      this.formDataFile
        ? this.formEl.setValue(this.formData)
        : this.formEl.setValue(this.currentUpload);
    } else {
      this.formEl.setValue(this.currentUpload);
    }
  }

  createUploadSavePack(data) {
    return {
      coordinates:
        data && data.GPSLatitude && data.GPSLongitude
          ? this.getImageMetaCoordinates(data)
          : {
              latitude: null,
              longitude: null,
            },
      date: data.DateTimeOriginal
        ? moment(data.DateTimeOriginal, Config.DATE_FORMAT_BACKEND).format(
            Config.DATE_FORMAT_BACKEND
          )
        : null,
      data: data ? data : null,
      name: this.name,
    };
  }

  checkImageMeta() {
    const fileReaderGetTags: FileReader = new FileReader();
    fileReaderGetTags.addEventListener('load', () => {
      const data = EXIF.readFromBinaryFile(fileReaderGetTags.result);
      this.uploadSavePack = this.createUploadSavePack(data);
      this.phUploadSuccessOptions.emit(this.uploadSavePack);
      if (this.requiredTags) {
        if (!data) {
          // no meta-tags
          // this.throwErrorMetaTags(
          //   Config.PHOTO_ERROR_NO_META,
          //   this.translate.instant('PROJECT.upload_error'), this.translate.instant('PROJECT.image_hag_create'));
        } else if (!data.Software || data.Software.search('Adobe') > 0) {
          // have meta-tags, but was modified
          // this.throwErrorMetaTags(
          //   Config.PHOTO_ERROR_WAS_MODIFIED,
          //   this.translate.instant('PROJECT.image_was_modified'), this.translate.instant('PROJECT.upload_error'));
        } else if (
          !data.GPSLatitude ||
          !data.GPSLongitude ||
          !data.GPSLatitudeRef ||
          !data.GPSLongitudeRef
        ) {
          // no GPS
          // this.throwErrorMetaTags(
          //   Config.PHOTO_ERROR_NO_GPS,
          //   this.translate.instant('PROJECT.set_coordinates'), this.translate.instant('PROJECT.no_meta'));
        }
      } else {
        this.addCorrectlyImg();
      }
    });
    fileReaderGetTags.readAsArrayBuffer(this.blobFile);
  }

  throwErrorMetaTags(message, title, desc) {
    // this.loading = false;
    // this.blobFile = null;
    // this.elDom.nativeElement.value = '';
    this.toast.error(desc, title);
    this.phUploadErrorOptions.emit(message);
  }

  addCorrectlyImg() {
    this.formData = new FormData();
    this.formData.append(this.name, this.blobFile);
    const fileReaderGetBase64: FileReader = new FileReader();
    fileReaderGetBase64.readAsDataURL(this.blobFile);
    // we must always create base64 because you must show this img on page
    fileReaderGetBase64.onload = () => {
      this.currentUpload = fileReaderGetBase64.result; // add to <img> tag
      this.phUploadSuccess.emit({ event: fileReaderGetBase64.result, name: this.name }); // add to cropper img
      this.formDataFile
        ? this.formEl.setValue(this.formData)
        : this.formEl.setValue(fileReaderGetBase64.result);
      this.elDom.nativeElement.value = ''; // clear input
      this.loading = false;
      document.body.removeAttribute('style');
    };
  }

  getImageMetaCoordinates(data): Coordinates {
    if (data.GPSLatitude && data.GPSLongitude && data.GPSLatitudeRef && data.GPSLongitudeRef) {
      const lat = data.GPSLatitude;
      const long = data.GPSLongitude;
      const strLatRef = data.GPSLatitudeRef || 'N';
      const strLongRef = data.GPSLongitudeRef || 'W';
      return {
        latitude: (lat[0] + lat[1] / 60 + lat[2] / 3600) * (strLatRef === 'N' ? 1 : -1),
        longitude: (long[0] + long[1] / 60 + long[2] / 3600) * (strLongRef === 'W' ? -1 : 1),
      };
    } else {
      return {
        latitude: null,
        longitude: null,
      };
    }
  }

  openUploadDialog() {
    this.elDom.nativeElement.click();
  }
}
